import { Card, Col, Flex, Row, Skeleton } from 'antd';
import TagComponent from 'components/Tags';
import { useRoleBasedCountryFilter } from 'components/UniversalFilters/RBCountryFilter';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isNotNullUndefined } from 'utils/validations';
import useDashboardV3Query from '../../hooks/queries/useDashboardV3Query';
import './style.scss';

const DashboardWidget = ({ seasonId }: { seasonId: string }) => {
  const [country] = useRoleBasedCountryFilter();
  const { data: { data } = {}, isPending: isLoading } = useDashboardV3Query({
    seasonId,
    country,
  });
  const { t } = useTranslation();

  const dashboardData = useMemo(
    () => [
      {
        title: t('dashboard.participatingFarmerGroups'),
        value: data?.activeGroup ?? 0,
        tags: [
          {
            label: t('status.Active'),
            value: data?.activeGroupPercent ?? 0,
          },
          {
            label: t('status.Harvested'),
            value: data?.harvestedGroupPercent ?? 0,
          },
        ],
      },
      {
        title: t('dashboard.participatingFarmers'),
        value: data?.activeFarmer ?? 0,
        tags: [
          {
            label: t('status.Active'),
            value: data?.activeFarmerPercent ?? 0,
          },
          {
            label: t('status.Harvested'),
            value: data?.harvestedFarmerPercent ?? 0,
          },
        ],
      },
      {
        title: t('dashboard.participatingHectares'),
        value: data?.activeParticipatedArea ?? 0,
        tags: [
          {
            label: t('status.Active'),
            value: data?.activeParticipatedAreaPercent ?? 0,
          },
          {
            label: t('status.Harvested'),
            value: data?.harvestedParticipatedAreaPercent ?? 0,
          },
        ],
      },
      {
        title: t('dashboard.installedPipes'),
        count: data?.installedPipes ?? 0,
        total: data?.expectedPipes ?? 0,
        subText: t('dashboard.installedPerExpected'),
      },
      {
        title: t('seasonProgressBar.Drying Event 1'),
        count: data?.servicedPipes ?? 0,
        total: data?.installedPipes ?? 0,
        subText: t('dashboard.servicedPerInstalled'),
      },
      {
        title: t('seasonProgressBar.Drying Event 2'),
        count: data?.secondServicedPipes ?? 0,
        total: data?.secondExpectedPipes ?? 0,
        subText: t('dashboard.servicedPerInstalled'),
      },
    ],
    [data],
  );

  return (
    <Row className="dashboard-widget" gutter={[16, 16]}>
      {dashboardData.map((item) => (
        <Col key={item.title} span={24} md={8}>
          <Card>
            {isLoading ? (
              <Skeleton active loading={isLoading} paragraph={{ rows: 2 }} />
            ) : (
              <Flex vertical>
                <span className="text-tertiary">{item.title}</span>
                {isNotNullUndefined(item.value) && (
                  <span className="text-display-large text-strong">
                    {item.value}
                  </span>
                )}
                {isNotNullUndefined(item.count) && (
                  <span>
                    <span className="heading-3">{item.count}</span>
                    <span className="text-secondary">/{item.total}</span>
                  </span>
                )}
                {item.subText && (
                  <span className="text-tertiary">{item.subText}</span>
                )}
                {item.tags && (
                  <Flex gap={8} wrap="wrap">
                    {item.tags.map((tag, index) => (
                      <TagComponent
                        key={tag.label}
                        color={index === 0 ? 'blue' : 'success'}
                        label={`${tag.value}% ${tag.label}`}
                      />
                    ))}
                  </Flex>
                )}
              </Flex>
            )}
          </Card>
        </Col>
      ))}
    </Row>
  );
};

export default DashboardWidget;
